import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  countryName,
  findByIso,
  matchDial,
  primaryByDial,
  searchCountries,
} from '../data/countryCodes';

const PHONE_UI = {
  'zh-Hant': { searchPh: '搜尋國家或國碼', notFound: '找不到符合的國家' },
  en: { searchPh: 'Search country or code', notFound: 'No matching country' },
};

function resolvePhoneLang(explicit) {
  if (explicit) {
    const s = String(explicit).toLowerCase();
    return s.startsWith('en') ? 'en' : 'zh-Hant';
  }
  try {
    const stored = String(localStorage.getItem('venue_i18n_lang') || 'zh-Hant');
    return stored.toLowerCase().startsWith('en') ? 'en' : 'zh-Hant';
  } catch {
    return 'zh-Hant';
  }
}

const DEFAULT_ISO = 'TW';

/**
 * 拆分 E.164 值 → { countryCode, number, iso }
 * "+886912345678" → { countryCode: "886", number: "912345678", iso: "TW" }
 * 未帶 + 的值視為台灣本地號碼。
 */
export function splitPhone(value) {
  if (!value || typeof value !== 'string') return { countryCode: '886', number: '', iso: DEFAULT_ISO };
  const trimmed = value.replace(/\s/g, '');
  if (!trimmed.startsWith('+')) return { countryCode: '886', number: trimmed, iso: DEFAULT_ISO };

  const digits = trimmed.slice(1);
  const dial = matchDial(digits);
  if (!dial) return { countryCode: '886', number: digits, iso: DEFAULT_ISO };
  const primary = primaryByDial(dial);
  return { countryCode: dial, number: digits.slice(dial.length), iso: primary ? primary.iso : DEFAULT_ISO };
}

/**
 * 格式化顯示："+886912345678" → "+886 912345678"
 */
export function formatPhoneDisplay(phone) {
  if (!phone) return '';
  const { countryCode, number } = splitPhone(phone);
  if (!number) return phone;
  return `+${countryCode} ${number}`;
}

/**
 * PhoneInput — 國際電話輸入元件（全球國碼 + 可輸入搜尋）
 *
 * Props:
 *   value       — E.164 格式字串，如 "+886912345678"
 *   onChange    — 回傳 E.164 格式字串
 *   disabled    — 是否停用
 *   placeholder — 號碼欄位的 placeholder
 *   className   — 外層 className
 *   inputClassName — 號碼 input 的 className
 *   selectClassName — 國碼按鈕的 className
 *   style       — 外層 inline style
 *   inputStyle  — 號碼 input 的 inline style
 *   selectStyle — 國碼按鈕的 inline style
 */
export default function PhoneInput({
  value = '',
  onChange,
  disabled = false,
  placeholder = '電話號碼',
  className = '',
  inputClassName = '',
  selectClassName = '',
  style = {},
  inputStyle = {},
  selectStyle = {},
  lang: langProp,
  ...rest
}) {
  const lang = useMemo(() => resolvePhoneLang(langProp), [langProp]);
  const ui = PHONE_UI[lang] || PHONE_UI['zh-Hant'];

  const { countryCode, number, iso: isoFromValue } = useMemo(() => splitPhone(value), [value]);

  // 同一國碼可能對應多個國家（+1、+44），使用者手動選過就記住，避免顯示被代表國覆蓋
  const [isoOverride, setIsoOverride] = useState(null);
  const activeIso = useMemo(() => {
    const override = isoOverride ? findByIso(isoOverride) : null;
    if (!override) return isoFromValue;
    // 還沒輸入號碼時值為空，沒有國碼可比對，沿用使用者選的國家
    if (!number) return override.iso;
    if (override.dial === countryCode) return override.iso;
    return isoFromValue;
  }, [isoOverride, countryCode, isoFromValue, number]);

  const country = findByIso(activeIso) || findByIso(DEFAULT_ISO);

  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [highlight, setHighlight] = useState(0);

  const wrapRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);

  const options = useMemo(
    () => (open ? searchCountries(keyword, lang) : searchCountries('', lang)),
    [open, keyword, lang],
  );

  const closeMenu = useCallback(() => {
    setOpen(false);
    setKeyword('');
    setHighlight(0);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) closeMenu();
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open, closeMenu]);

  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus?.(), 0);
  }, [open]);

  // 鍵盤移動時讓選項保持在可見範圍
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.children[highlight];
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [open, highlight]);

  const stripTrunkZero = (target, raw) => {
    if (target?.trunkZero && raw.startsWith('0')) return raw.replace(/^0+/, '');
    return raw;
  };

  // 只有國碼、沒有號碼時回傳空字串，避免 "+81" 這種值通過呼叫端的必填檢查
  const emit = (target, raw) => {
    const local = stripTrunkZero(target, raw);
    onChange?.(local ? `+${target.dial}${local}` : '');
  };

  const pickCountry = (next) => {
    setIsoOverride(next.iso);
    closeMenu();
    emit(next, number);
  };

  const handleNumberChange = (e) => {
    emit(country, e.target.value.replace(/\D/g, ''));
  };

  const onSearchKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, options.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const picked = options[highlight];
      if (picked) pickCountry(picked);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closeMenu();
    }
  };

  const uiFont = parseFloat(selectStyle.fontSize) || parseFloat(inputStyle.fontSize) || 15;

  const wrapStyle = {
    display: 'flex',
    gap: 0,
    width: '100%',
    position: 'relative',
    ...style,
  };

  const triggerStyle = {
    padding: '8px 4px',
    fontSize: 15,
    border: '1px solid #ccc',
    borderRight: 'none',
    borderRadius: '8px 0 0 8px',
    background: '#fafafa',
    minWidth: 110,
    flexShrink: 0,
    boxSizing: 'border-box',
    cursor: disabled ? 'not-allowed' : 'pointer',
    color: 'inherit',
    textAlign: 'left',
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    ...selectStyle,
  };

  const defaultInputStyle = {
    flex: 1,
    padding: '8px 12px',
    fontSize: 15,
    border: '1px solid #ccc',
    borderRadius: '0 8px 8px 0',
    minWidth: 0,
    boxSizing: 'border-box',
    ...inputStyle,
  };

  // E.164 全長上限 15 碼（含國碼）
  const numberMaxLength = Math.max(4, 15 - country.dial.length);

  return (
    <div ref={wrapRef} className={className} style={wrapStyle} {...rest}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          const tw = findByIso('TW');
          if (tw) pickCountry(tw);
        }}
        style={{
          marginRight: 6,
          padding: '8px 10px',
          border: '1px solid #ccc',
          borderRadius: 8,
          background: '#fff',
          cursor: disabled ? 'not-allowed' : 'pointer',
          flexShrink: 0,
        }}
      >
        +886
      </button>
      <button
        type="button"
        className={selectClassName}
        onClick={() => !disabled && (open ? closeMenu() : setOpen(true))}
        disabled={disabled}
        style={triggerStyle}
        title={`${countryName(country, lang)} +${country.dial}`}
      >
        <span>{country.flag}</span>
        <span>+{country.dial}</span>
        <span style={{ fontSize: Math.round(uiFont * 0.7), opacity: 0.6 }}>▾</span>
      </button>

      <input
        className={inputClassName}
        type="tel"
        inputMode="numeric"
        value={number}
        onChange={handleNumberChange}
        disabled={disabled}
        placeholder={placeholder}
        style={defaultInputStyle}
        maxLength={numberMaxLength}
      />

      {open && (
        <div
          style={{
            position: 'absolute',
            zIndex: 2000,
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            minWidth: 240,
            background: '#fff',
            border: '1px solid #d1d5db',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.16)',
            overflow: 'hidden',
          }}
        >
          <div style={{ padding: 8, borderBottom: '1px solid #f1f5f9' }}>
            <input
              ref={searchRef}
              value={keyword}
              onChange={(e) => { setKeyword(e.target.value); setHighlight(0); }}
              onKeyDown={onSearchKeyDown}
              placeholder={ui.searchPh}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '8px 10px',
                fontSize: uiFont,
                border: '1px solid #d1d5db',
                borderRadius: 6,
                outline: 'none',
                color: '#111',
                background: '#fff',
              }}
            />
          </div>

          <div ref={listRef} style={{ maxHeight: 260, overflowY: 'auto' }}>
            {options.length === 0 ? (
              <div style={{ padding: '14px 12px', fontSize: uiFont, color: '#94a3b8', textAlign: 'center' }}>
                {ui.notFound}
              </div>
            ) : (
              options.map((c, idx) => {
                const selected = c.iso === country.iso;
                return (
                  <div
                    key={c.iso}
                    onMouseEnter={() => setHighlight(idx)}
                    onClick={() => pickCountry(c)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '8px 12px',
                      fontSize: uiFont,
                      cursor: 'pointer',
                      color: '#111',
                      background: idx === highlight ? '#eef2ff' : selected ? '#f8fafc' : '#fff',
                      fontWeight: selected ? 700 : 400,
                    }}
                  >
                    <span style={{ fontSize: Math.round(uiFont * 1.1) }}>{c.flag}</span>
                    <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {countryName(c, lang)}
                    </span>
                    <span style={{ color: '#64748b' }}>+{c.dial}</span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
